/**
 * Утилиты для работы с реакциями на сообщения чата.
 *
 * Инвариант: один пользователь — одна реакция на сообщение.
 * Сервер может возвращать несколько реакций от одного пользователя
 * (разные эмодзи) — эти утилиты нормализуют массив, оставляя только
 * последнюю реакцию каждого пользователя.
 */

/**
 * Возвращает идентификатор пользователя-владельца реакции.
 * Реакция может содержать userId, profileId или оба поля.
 * @param {Object} r — объект реакции
 * @returns {string|null}
 */
function getReactionOwnerId(r) {
    return r?.userId || r?.profileId || null;
}

/**
 * Нормализует массив реакций: оставляет только одну реакцию на пользователя.
 * Если у пользователя несколько реакций — оставляется последняя в массиве
 * (предполагается, что она самая свежая).
 *
 * @param {Array} reactions — массив реакций (MessageReactionResponse[])
 * @returns {Array} нормализованный массив без дубликатов по пользователю
 */
export function normalizeReactions(reactions) {
    if (!Array.isArray(reactions) || reactions.length === 0) return [];

    const byUser = new Map();
    for (const r of reactions) {
        const ownerId = getReactionOwnerId(r);
        if (!ownerId) {
            // Нет идентификатора — оставляем как есть (не должно случаться)
            byUser.set(`__anon_${r.emoji}_${Math.random()}`, r);
            continue;
        }
        byUser.set(ownerId, r);
    }

    return Array.from(byUser.values());
}

/**
 * Находит реакцию текущего пользователя в нормализованном массиве реакций.
 *
 * @param {Array} reactions — массив реакций (может содержать дубликаты)
 * @param {string} currentProfileId — ID профиля текущего пользователя
 * @returns {Object|null} реакция текущего пользователя или null
 */
export function findMyReaction(reactions, currentProfileId) {
    if (!currentProfileId || !Array.isArray(reactions)) return null;
    const normalized = normalizeReactions(reactions);
    return normalized.find(
        r => r.userId === currentProfileId || r.profileId === currentProfileId
    ) || null;
}
